import { ElementType } from '@urbanx/agx-ui-components';
import type { VendorModel } from '@urbanx/agx.campaigns.client';
import isEqual from 'lodash-es/isEqual';
import { getExistingValue, updateFormValue } from 'features/form/formReducer';
import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import { useFormSaver } from './useFormSaver';
import { useToaster } from './useToaster';

const getIdentityVerificationValues = (participants?: VendorModel[] | null) =>
  (participants ?? [])
    .flatMap((participant) => [
      participant.individual,
      ...(participant.powerOfAttorney?.attorneys ?? []),
      ...(participant.company?.signatories ?? []),
      ...(participant.deceasedEstate?.executors ?? []),
    ])
    .filter(
      (person) =>
        person?.identityVerification || person?.riskAssessment || person?.employmentDetails
    )
    .map((person) => ({
      identityVerification: person?.identityVerification,
      riskAssessment: person?.riskAssessment,
      employmentDetails: person?.employmentDetails,
    }));

export const useFormValueUpdater = (clearValidation: () => void) => {
  const form = useAppSelector((state) => state.form);
  const dispatch = useAppDispatch();
  const saveForm = useFormSaver();
  const { showErrorToast } = useToaster();
  const [saveRequest, setSaveRequest] = useState(0);
  const handledSaveRequest = useRef(0);

  useEffect(() => {
    if (saveRequest === handledSaveRequest.current) return;
    handledSaveRequest.current = saveRequest;
    // Wait for the updated selector values before calling the existing save hook.
    void saveForm(form.selectedForm?.formConfigType).catch((error: unknown) => {
      showErrorToast(error instanceof Error ? error.message : 'Failed to save form.');
    });
  }, [saveRequest, saveForm, form.selectedForm?.formConfigType, showErrorToast]);

  return (
    change: Parameters<typeof updateFormValue>[0],
    elementType: ElementType,
    saveChanges = false
  ) => {
    const identityVerificationChanged =
      elementType === ElementType.VendorBuyerDetails &&
      !isEqual(
        getIdentityVerificationValues(
          getExistingValue([...(change.parentPathIds ?? []), change.id], form.formValues)
        ),
        getIdentityVerificationValues(change.value)
      );
    clearValidation();
    dispatch(updateFormValue(change));

    if (saveChanges || identityVerificationChanged) {
      setSaveRequest((request) => request + 1);
    }
  };
};
